import type { WebPushAutoPromptConfig } from "@jti/fe-common";

export type WebPushPromptState = {
  lastAttemptAt?: number;
  attempts: number;
  denied?: boolean;
  subscribed?: boolean;
};

export type PromptAvailability =
  | { eligible: true; state: WebPushPromptState; storageKey: string }
  | {
      eligible: false;
      reason: "cooldown" | "cap" | "permission_resolved" | "storage_unavailable";
    };

const DAY_IN_MS = 24 * 60 * 60 * 1000;

export const getWebPushStorageKey = (tenant: string, locale: string, version: string) =>
  `webPushAutoPrompt:${encodeURIComponent(tenant)}:${encodeURIComponent(locale)}:${encodeURIComponent(version)}`;

const isValidState = (state: unknown): state is WebPushPromptState => {
  if (!state || typeof state !== "object") return false;

  const candidate = state as Partial<WebPushPromptState>;
  return (
    Number.isInteger(candidate.attempts) &&
    Number(candidate.attempts) >= 0 &&
    (candidate.lastAttemptAt === undefined || Number.isFinite(candidate.lastAttemptAt)) &&
    (candidate.denied === undefined || typeof candidate.denied === "boolean") &&
    (candidate.subscribed === undefined || typeof candidate.subscribed === "boolean")
  );
};

const assertStorageAvailable = () => {
  const probeKey = "__webPushStorageProbe__";
  localStorage.setItem(probeKey, "1");
  localStorage.removeItem(probeKey);
};

export const readWebPushPromptState = (storageKey: string): WebPushPromptState | null => {
  const rawState = localStorage.getItem(storageKey);
  if (!rawState) return null;

  const state: unknown = JSON.parse(rawState);
  if (!isValidState(state)) throw new Error("Invalid web push prompt state");

  return state;
};

export const writeWebPushPromptState = (storageKey: string, state: WebPushPromptState) => {
  localStorage.setItem(storageKey, JSON.stringify(state));
};

export const getPromptAvailability = ({
  config,
  tenant,
  locale,
  permission,
  now = Date.now(),
}: {
  config: Required<WebPushAutoPromptConfig>;
  tenant: string;
  locale: string;
  permission: NotificationPermission;
  now?: number;
}): PromptAvailability => {
  if (permission === "denied") return { eligible: false, reason: "permission_resolved" };

  const storageKey = getWebPushStorageKey(tenant, locale, config.configurationVersion);

  try {
    assertStorageAvailable();
    const state = readWebPushPromptState(storageKey) ?? { attempts: 0 };

    if (state.denied) return { eligible: false, reason: "permission_resolved" };
    if (permission === "granted") {
      if (state.attempts === 0 || state.subscribed) return { eligible: false, reason: "permission_resolved" };
      if (state.lastAttemptAt && now - state.lastAttemptAt < config.cooldownDays * DAY_IN_MS) {
        return { eligible: false, reason: "cooldown" };
      }
      return { eligible: true, state, storageKey };
    }
    if (state.attempts >= config.maxImpressions) return { eligible: false, reason: "cap" };
    if (state.lastAttemptAt && now - state.lastAttemptAt < config.cooldownDays * DAY_IN_MS) {
      return { eligible: false, reason: "cooldown" };
    }

    return { eligible: true, state, storageKey };
  } catch {
    return { eligible: false, reason: "storage_unavailable" };
  }
};

export const recordWebPushAttempt = (
  storageKey: string,
  state: WebPushPromptState,
  now = Date.now()
): WebPushPromptState => {
  const attemptedState = {
    ...state,
    lastAttemptAt: now,
    attempts: state.attempts + 1,
  };
  writeWebPushPromptState(storageKey, attemptedState);
  return attemptedState;
};

export const recordWebPushSubscriptionRetry = (
  storageKey: string,
  state: WebPushPromptState,
  now = Date.now()
): WebPushPromptState => {
  const attemptedState = {
    ...state,
    lastAttemptAt: now,
  };
  writeWebPushPromptState(storageKey, attemptedState);
  return attemptedState;
};
